/**
 * Auth Provider - Authentication state management
 *  * 
 * Features:
 * - User authentication state
 * - Remember Me support (extended token duration)
 * - Automatic token refresh on activity
 * - Permission checking
 * - After sign-in, back to the page first asked for (same-origin paths only)
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { returnPathFromState } from '@/lib/safe-return-path';
import { api, setAuthTokens } from '@/services/api';
import { isUnauthorizedError, withSessionRetry } from './session-retry';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  permissions: string[];
  twoFactorEnabled?: boolean;
}

// Normalize user from different API response formats
function normalizeUser(data: Record<string, unknown>): User {
  // Handle /auth/me response (role is an object)
  if (data.role && typeof data.role === 'object') {
    const roleObj = data.role as { name: string; permissions: { code?: string }[] | string[] };
    return {
      id: data.id as string,
      email: data.email as string,
      firstName: data.firstName as string | undefined,
      lastName: data.lastName as string | undefined,
      role: roleObj.name,
      permissions: roleObj.permissions.map((p) =>
        typeof p === 'string' ? p : p.code || ''
      ).filter(Boolean),
      twoFactorEnabled: data.twoFactorEnabled as boolean | undefined,
    };
  }

  // Handle /auth/login response (role is a string, permissions at top level)
  return {
    id: data.id as string,
    email: data.email as string,
    firstName: data.firstName as string | undefined,
    lastName: data.lastName as string | undefined,
    role: data.role as string,
    permissions: (data.permissions as string[]) || [],
  };
}

/** Returned by login() when the account has two-factor auth enabled. */
export interface TwoFactorChallenge {
  requires2fa: true;
  challengeToken: string;
}

/** Self-registration payload (public client signup). */
export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (
    email: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<TwoFactorChallenge | undefined>;
  register: (
    data: RegisterData,
  ) => Promise<{ requiresEmailVerification: boolean }>;
  verify2fa: (challengeToken: string, code: string) => Promise<void>;
  logout: () => void;
  refreshMe: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'nord-living-token';
const REFRESH_KEY = 'nord-living-refresh';
const TOKEN_EXPIRY_KEY = 'nord-living-token-expiry';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // The page ProtectedRoute sent to /login from (validated: same-origin path).
  const location = useLocation();
  const returnTo = useRef<string | null>(null);
  returnTo.current = returnPathFromState(location.state);

  useEffect(() => {
    const onTokensUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ expiryTime?: number }>).detail;
      if (detail?.expiryTime) {
        scheduleLogout(detail.expiryTime);
      } else {
        scheduleLogoutFromStorage();
      }
    };

    const onAuthCleared = () => {
      scheduleLogout(0); // clears timer
      setUser(null);
      queryClient.clear();
    };

    window.addEventListener('nord-living:tokens-updated', onTokensUpdated as EventListener);
    window.addEventListener('nord-living:auth-cleared', onAuthCleared);
    return () => {
      window.removeEventListener('nord-living:tokens-updated', onTokensUpdated as EventListener);
      window.removeEventListener('nord-living:auth-cleared', onAuthCleared);
    };
  }, [queryClient]);

  const scheduleLogout = useCallback(
    (expiryTimeMs: number) => {
      const w = window as unknown as { __projectTemplateLogoutTimer?: number };
      if (w.__projectTemplateLogoutTimer) {
        window.clearTimeout(w.__projectTemplateLogoutTimer);
        w.__projectTemplateLogoutTimer = undefined;
      }

      if (!expiryTimeMs || expiryTimeMs <= Date.now()) {
        return;
      }

      // Shortly before the access token expires, keep the session alive with
      // the refresh token (the request interceptor refreshes proactively).
      // Without a refresh token (e.g. impersonation) the session simply ends.
      const bufferMs = 60_000;
      const delay = Math.max(0, expiryTimeMs - Date.now() - bufferMs);
      w.__projectTemplateLogoutTimer = window.setTimeout(() => {
        if (localStorage.getItem(REFRESH_KEY)) {
          void api.get('/auth/me').catch(() => undefined);
          return;
        }
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
        setUser(null);
        queryClient.clear();
        navigate('/login');
      }, delay);
    },
    [navigate, queryClient],
  );

  const scheduleLogoutFromStorage = useCallback(() => {
    const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
    const expiry = expiryStr ? Number.parseInt(expiryStr, 10) : 0;
    if (Number.isFinite(expiry) && expiry > 0) {
      scheduleLogout(expiry);
    }
  }, [scheduleLogout]);

  const refreshMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      return;
    }
    const response = await api.get('/auth/me');
    setUser(normalizeUser(response.data.data));
    scheduleLogoutFromStorage();
  }, [scheduleLogoutFromStorage]);

  // Check for existing session on mount. Only a real 401 ends the session;
  // transient failures (429 rate limit, 5xx, network) are retried so a busy
  // moment never signs the user out.
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        try {
          await withSessionRetry(refreshMe);
        } catch (error) {
          if (isUnauthorizedError(error)) {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_KEY);
            localStorage.removeItem(TOKEN_EXPIRY_KEY);
            queryClient.clear();
          }
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, [queryClient, refreshMe]);

  const completeLogin = useCallback(
    (data: Record<string, unknown>) => {
      const { user: userData, accessToken, refreshToken, expiresIn } = data as {
        user: Record<string, unknown>;
        accessToken: string;
        refreshToken: string;
        expiresIn?: number;
      };

      // Store tokens with expiry tracking
      setAuthTokens(accessToken, refreshToken, expiresIn || 900);
      setUser(normalizeUser(userData));

      // Studio routes send a client on to /portal (and the reverse) on arrival.
      navigate(returnTo.current ?? '/today');
    },
    [navigate],
  );

  const login = useCallback(
    async (
      email: string,
      password: string,
      rememberMe = false,
    ): Promise<TwoFactorChallenge | undefined> => {
      const response = await api.post('/auth/login', {
        email,
        password,
        rememberMe,
      });
      const data = response.data.data;

      // Account has 2FA enabled — caller must collect a code and verify2fa()
      if (data.requires2fa) {
        return { requires2fa: true, challengeToken: data.challengeToken };
      }

      completeLogin(data);
      return undefined;
    },
    [completeLogin],
  );

  const register = useCallback(
    async (
      data: RegisterData,
    ): Promise<{ requiresEmailVerification: boolean }> => {
      const response = await api.post('/auth/register', data);
      const payload = (response.data.data ?? {}) as Record<string, unknown>;
      // Email verification off → tokens returned → auto-login.
      if (payload.accessToken) {
        completeLogin(payload);
        return { requiresEmailVerification: false };
      }
      return {
        requiresEmailVerification: Boolean(payload.requiresEmailVerification),
      };
    },
    [completeLogin],
  );

  const verify2fa = useCallback(
    async (challengeToken: string, code: string) => {
      const response = await api.post('/auth/2fa/verify', {
        challengeToken,
        code,
      });
      completeLogin(response.data.data);
    },
    [completeLogin],
  );

  const logout = useCallback(() => {
    // Best-effort server-side session revocation (bumps tokenVersion). Capture
    // the token and send it explicitly — the async request interceptor would
    // otherwise read it from localStorage AFTER we clear it below. Never block
    // the local logout on the network call.
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      void api
        .post('/auth/logout', undefined, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .catch(() => undefined);
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    setUser(null);
    queryClient.clear();
    navigate('/login');
  }, [navigate, queryClient]);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!user?.permissions) return false;

      // Admin has all permissions
      if ((user.role || '').toLowerCase() === 'admin') return true;

      return user.permissions.includes(permission);
    },
    [user],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        verify2fa,
        logout,
        refreshMe,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
